import { SlickGrid, SlickDataView } from "slickgrid";
import "jquery";
import compareMultiType from "./utils/compare-multi-type";

/**
 * FundsGridTable - A specialized table implementation for Funds
 * Tailored to handle fund-specific data structures and requirements
 */
class FundsGridTable {
  // option properties
  data = [];
  columnDefinitions = [];
  sortDefault = { column: "", direction: "" };
  selectedColumns = [];
  tableTarget = "";
  options = {
    editable: false,
    enableAddRow: false,
    enableCellNavigation: true,
    asyncEditorLoading: true,
    autoEdit: false,
    enableAsyncPostRender: true,
    syncColumnCellResize: true,
    autoHeight: false, // Use calculated height instead
    enableAutoResize: true,
    multiColumnSort: true,
    rowHeight: 32,
    enableResponsiveResize: false
  };

  // responsive options
  responsiveOptions = {
    rowHeight: 32,
    minVisibleRows: 10,
    rowHeaderHeight: 30,
    headerOffsetPx: 250
  };

  // table properties
  dataView;
  grid;

  // callback properties
  onSortCallback = null;
  onSearchCallback = null;

  /**
   * Transform fund data into table data format
   * Handles fund-specific identifiers (ISIN, APIR, ARSN) and ensures unique IDs
   */
  jsonToTableData(data) {
    return data.map((fund, index) => {
      const result = {};

      // Include all fields from the fund object
      Object.keys(fund).forEach(key => {
        result[key] = fund[key];
      });

      // Ensure id field exists - use slug, id, or generate one
      // Priority: slug > id > identifiers (APIR, ARSN, ISIN, ASX Code) > generated
      if (!result.id || result.id === null || result.id === undefined) {
        result.id = result.slug || 
                   result['slug'] || 
                   result.id || 
                   result['id'] ||
                   result.apir || 
                   result.arsn || 
                   result.isin || 
                   result.asx_code ||
                   `fund-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Ensure id is always a string
      result.id = String(result.id).trim();
      if (result.id === '' || result.id === 'null' || result.id === 'undefined') {
        result.id = `fund-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Also set slug for consistency
      if (!result.slug) {
        result.slug = result.id;
      }

      return result;
    });
  }

  constructor({
    data,
    columnDefinitions,
    sortDefault,
    selectedColumns,
    tableTarget,
    options = {},
    responsiveOptions = {},
    onSort = null,
    onSearch = null
  }) {
    this.data = data || [];
    this.columnDefinitions = columnDefinitions || [];
    this.sortDefault = sortDefault || { column: "", direction: "" };
    this.selectedColumns = selectedColumns || [];
    this.tableTarget = tableTarget || "fund-table";
    this.options = { ...this.options, ...options };
    this.responsiveOptions = { ...this.responsiveOptions, ...responsiveOptions };
    this.onSortCallback = onSort;
    this.onSearchCallback = onSearch;

    // Transform data to table format
    this.data = this.jsonToTableData(this.data);

    // Validate all items have id
    const itemsWithoutId = this.data.filter(item => !item.id || item.id === null || item.id === undefined);
    if (itemsWithoutId.length > 0) {
      console.error('FundsGridTable: Some funds are missing id property:', itemsWithoutId);
      // Fix them
      itemsWithoutId.forEach((item, idx) => {
        item.id = `fund-fallback-${Date.now()}-${idx}`;
        item.slug = item.id;
      });
    }

    this.renderTable();
  }

  /**
   * Render the SlickGrid table
   */
  renderTable() {
    const gridContainer = document.getElementById(this.tableTarget);
    if (!gridContainer) {
      console.error(`FundsGridTable: Container with id "${this.tableTarget}" not found`);
      return;
    }

    // Filter columns based on selectedColumns
    const visibleColumns = this.columnDefinitions.filter(col => 
      this.selectedColumns.includes(col.id) || this.selectedColumns.length === 0
    );

    // Map columns to SlickGrid format
    const columns = visibleColumns.map(col => {
      const field = (col.field || col.id).replace(/-/g, "_");
      
      // Use formatter from column definition (already converted to function in controller)
      // For research-report, create a wrapper to handle both field name formats
      let formatter = col.formatter;
      if (col.id === 'research-report') {
        const baseFormatter = formatter;
        formatter = (row, cell, value, columnDef, dataContext) => {
          const researchReport = value || dataContext.research_report || dataContext['research-report'] || dataContext['research_report'];
          if (researchReport && typeof researchReport === 'object' && researchReport.url) {
            const text = researchReport.text || 'View Report';
            return `<a href="${researchReport.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return baseFormatter ? baseFormatter(row, cell, researchReport, columnDef, dataContext) : '';
        };
      }
      // For research (Product Research link), always use a formatter so object isn't shown as "[object Object]"
      if (col.id === 'research') {
        const baseFormatter = formatter;
        formatter = (row, cell, value, columnDef, dataContext) => {
          const research = value || dataContext.research || dataContext['research'];
          if (research && typeof research === 'object' && research.url) {
            const text = research.text || 'View';
            return `<a href="${research.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return baseFormatter ? baseFormatter(row, cell, research, columnDef, dataContext) : '';
        };
      }
      
      return {
        id: col.id,
        name: col.name,
        field: field,
        width: col.width || 100,
        minWidth: col.minWidth || col.width || 100,
        sortable: col.sortable !== false,
        headerCssClass: col.headerCssClass || col.cssClass || 'text-center',
        cssClass: col.cssClass || 'text-center',
        formatter: formatter
      };
    });

    // Create DataView
    this.dataView = new SlickDataView({
      inlineFilters: false
    });

    // Set items
    this.dataView.setItems(this.data, "id");
    
    // Initialize with no filter (show all items)
    this.dataView.setFilter((item) => {
      return true
    });

    // Setup sorting
    this.setupSorting();

    // Setup search
    this.setupSearch();

    // Create grid
    this.grid = new SlickGrid(gridContainer, this.dataView, columns, this.options);

    // Setup event handlers
    this.setupEventHandlers();

    // Setup responsive resize if enabled
    if (this.options.enableResponsiveResize) {
      this.bindResizeHandler();
      this.resizeToViewport();
    }
  }

  /**
   * Bind resize handler for responsive table
   */
  bindResizeHandler() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.resizeHandler = () => {
      this.resizeToViewport();
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Resize table to fit viewport
   */
  resizeToViewport() {
    if (!this.grid || !this.options.enableResponsiveResize) return;

    const viewportHeight = window.innerHeight;
    const headerOffset = this.responsiveOptions.headerOffsetPx || 250;
    const rowHeaderHeight = this.responsiveOptions.rowHeaderHeight || 30;
    const rowHeight = this.responsiveOptions.rowHeight || 32;
    const minVisibleRows = this.responsiveOptions.minVisibleRows || 10;

    const minHeight = (minVisibleRows * rowHeight) + rowHeaderHeight;
    const maxHeight = viewportHeight - headerOffset;
    const calculatedHeight = Math.max(minHeight, maxHeight);

    const gridContainer = document.getElementById(this.tableTarget);
    if (gridContainer) {
      gridContainer.style.height = `${calculatedHeight}px`;
      if (this.grid && this.grid.resizeCanvas) {
        this.grid.resizeCanvas();
      }
    }
  }

  /**
   * Get formatter function for a column
   */
  getFormatter(formatterType, columnId) {
    if (!formatterType) {
      return null;
    }

    switch (formatterType) {
      case 'currency':
        return (row, cell, value) => {
          if (value === null || value === undefined || value === '') return '';
          const num = parseFloat(value);
          if (isNaN(num)) return value;
          return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 3
          }).format(num);
        };
      
      case 'currency0':
        return (row, cell, value) => {
          if (value === null || value === undefined || value === '') return '';
          const num = parseFloat(value);
          if (isNaN(num)) return value;
          return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(num);
        };
      
      case 'percent':
        return (row, cell, value) => {
          if (value === null || value === undefined || value === '') return '';
          const num = parseFloat(value);
          if (isNaN(num)) return value;
          return `${num.toFixed(2)}%`;
        };
      
      case 'date':
        return (row, cell, value) => {
          if (!value) return '';
          const date = new Date(value);
          if (isNaN(date.getTime())) return value;
          return date.toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        };
      
      case 'link':
        return (row, cell, value) => {
          if (!value || typeof value !== 'object') return '';
          if (value.url && value.text) {
            return `<a href="${value.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline">${value.text}</a>`;
          }
          return '';
        };
      
      // Special formatter for research-report column
      case 'research-report':
        return (row, cell, value) => {
          if (!value || typeof value !== 'object') return '';
          if (value.url) {
            const text = value.text || 'View Report';
            return `<a href="${value.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return '';
        };

      // Special formatter for research column (Product Research link with date text)
      case 'research':
        return (row, cell, value) => {
          if (!value || typeof value !== 'object') return '';
          if (value.url) {
            const text = value.text || '';
            return `<a href="${value.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return '';
        };

      default:
        return null;
    }
  }

  /**
   * Setup sorting functionality
   */
  setupSorting() {
    if (!this.dataView) return;

    const sortCol = this.sortDefault.column || 'name';
    const sortDir = this.sortDefault.direction || 'asc';
    const sign = sortDir === 'asc' ? 1 : -1;
    
    // Map column id to field name (handle both hyphen and underscore versions)
    const field = sortCol.replace(/-/g, "_");

    this.dataView.sort((item1, item2) => {
      // Try both field name formats
      let val1 = item1[field] !== undefined ? item1[field] : item1[sortCol];
      let val2 = item2[field] !== undefined ? item2[field] : item2[sortCol];

      const result = compareMultiType(val1, val2);
      return result * sign;
    }, sortDir === 'asc');
  }

  /**
   * Setup search functionality
   */
  setupSearch() {
    // Search will be handled by onSearch method
  }

  /**
   * Handle search input - searches through all loaded content in the table
   * Matches the behavior of SlickGridTable for securities
   */
  onSearch(searchTerm) {
    if (!this.dataView) {
      console.warn('FundsGridTable: dataView not available for search')
      return
    }

    const term = (searchTerm || '').toLowerCase().trim()

    this.dataView.setFilter((item) => {
      if (!term) return true

      // Search through ALL fields in the item
      // The data has both hyphen and underscore versions of field names
      // This matches the behavior of SlickGridTable which searches through all data
      for (const key in item) {
        if (item.hasOwnProperty(key) && item[key] != null) {
          // Skip internal/metadata fields
          if (key === 'id' || key === 'slug' || key === '_isTotals' || key === '_currency') {
            continue
          }

          // Handle different value types
          let value = item[key]
          
          // Skip objects (like research_report which is an object with url/text)
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // For objects, try to search in their string representation or specific properties
            if (value.url) {
              value = String(value.url).toLowerCase()
              if (value.includes(term)) return true
            }
            if (value.text) {
              value = String(value.text).toLowerCase()
              if (value.includes(term)) return true
            }
            continue
          }

          // Convert value to string and search
          value = String(value).toLowerCase()
          if (value.includes(term)) {
            return true
          }
        }
      }
      return false
    })

    // Refresh the dataView to apply the filter
    this.dataView.refresh()
    
    // Ensure the grid updates to show filtered results
    if (this.grid) {
      this.grid.invalidate()
      this.grid.render()
    }
  }

  /**
   * Setup event handlers
   */
  setupEventHandlers() {
    if (!this.grid) return;

    // Handle sort - match the Securities table implementation
    this.grid.onSort.subscribe((e, args) => {
      const cols = args.sortCols;

      this.dataView.sort((a, b) => {
        // If both have the same selection status, apply original sorting
        for (let i = 0; i < cols.length; i++) {
          const { sortCol, sortAsc } = cols[i];
          const field = sortCol.field;
          const sign = sortAsc ? 1 : -1;

          // Get the value - try both hyphen and underscore field name formats
          let val1 = a[field] !== undefined ? a[field] : a[field.replace(/-/g, "_")];
          let val2 = b[field] !== undefined ? b[field] : b[field.replace(/-/g, "_")];

          // If still undefined, try the original field name
          if (val1 === undefined) val1 = a[sortCol.id] || a[sortCol.id.replace(/-/g, "_")];
          if (val2 === undefined) val2 = b[sortCol.id] || b[sortCol.id.replace(/-/g, "_")];

          const result = compareMultiType(val1, val2);
          if (result !== 0) {
            return result * sign;
          }
        }
        return 0;
      });

      this.grid.invalidate();
      this.grid.render();

      if (this.onSortCallback) {
        this.onSortCallback(e, args);
      }
    });

    // Handle cell click for research report and research (Product Research) links
    // Check both hyphen and underscore field name formats
    this.grid.onClick.subscribe((e, args) => {
      const item = this.dataView.getItem(args.row);
      if (!item) return;

      const researchReport = item.research_report || item['research-report'] || item['research_report'];
      if (researchReport && typeof researchReport === 'object' && researchReport.url) {
        window.open(researchReport.url, '_blank');
        return;
      }

      const research = item.research || item['research'];
      if (research && typeof research === 'object' && research.url) {
        window.open(research.url, '_blank');
      }
    });
  }

  /**
   * Refresh the table with new data
   */
  refresh(data) {
    this.data = this.jsonToTableData(data || []);
    if (this.dataView) {
      this.dataView.setItems(this.data, "id");
      this.dataView.refresh();
    }
  }

  /**
   * Destroy the grid
   */
  destroy() {
    if (this.grid) {
      this.grid.destroy();
      this.grid = null;
    }
    if (this.dataView) {
      this.dataView = null;
    }
  }
}

export default FundsGridTable;

